import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { 
  ArrowLeft, 
  MessageSquare, 
  Paperclip, 
  Upload, 
  Download, 
  Trash2,
  Edit,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle
} from 'lucide-react';
import moment from 'moment';

const TodoDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [todo, setTodo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const fetchTodo = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/todos/${id}`);
      setTodo(response.data.data);
      setComments(response.data.data.comments || []);
      setAttachments(response.data.data.attachments || []);
    } catch (error) {
      toast.error('Failed to load todo');
      navigate('/todos');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchTodo();
  }, [fetchTodo]);

  const addComment = async () => {
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    try {
      const response = await api.post(`/todos/${id}/comments`, { comment: newComment });
      setComments([...comments, response.data.data]);
      setNewComment('');
      toast.success('Comment added successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add comment');
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      await api.delete(`/todos/${id}/comments/${commentId}`);
      setComments(comments.filter(c => (c.id || c._id) !== commentId));
      toast.success('Comment deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete comment');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setUploadingFile(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (document.getElementById('fileDescription').value) {
        formData.append('description', document.getElementById('fileDescription').value);
      }

      const response = await api.post(`/todos/${id}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setAttachments([response.data.data, ...attachments]);
      setSelectedFile(null);
      document.getElementById('fileDescription').value = '';
      document.getElementById('fileInput').value = '';
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const deleteAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) {
      return;
    }

    try {
      await api.delete(`/todos/${id}/attachments/${attachmentId}`);
      setAttachments(attachments.filter(a => (a.id || a._id) !== attachmentId));
      toast.success('Attachment deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete attachment');
    }
  };

  const downloadAttachment = async (attachmentId, fileName) => {
    try {
      const response = await api.get(`/todos/${id}/attachments/${attachmentId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      await api.put(`/todos/${id}`, { status: newStatus });
      setTodo({ ...todo, status: newStatus });
      setIsEditingStatus(false);
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'In Progress':
        return <Clock className="w-5 h-5 text-blue-600" />;
      case 'Cancelled':
        return <XCircle className="w-5 h-5 text-gray-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      case 'Cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!todo) {
    return <div>Todo not found</div>;
  }

  const isAssignedToCurrentUser = todo.assignedTo && (
    (todo.assignedTo.id || todo.assignedTo._id) === (user?.id || user?._id)
  );
  const canEdit = user?.role === 'Administrator' || user?.role === 'Supervisor' || isAssignedToCurrentUser;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/todos')}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{todo.title}</h1>
            <p className="text-gray-600 mt-1">
              Created {moment(todo.createdAt).format('MMM D, YYYY [at] h:mm A')}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {isEditingStatus ? (
            <div className="flex items-center space-x-2">
              <select
                value={todo.status}
                onChange={(e) => updateStatus(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              >
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
              <button
                onClick={() => setIsEditingStatus(false)}
                className="px-2 py-1 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              {getStatusIcon(todo.status)}
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(todo.status)}`}>
                {todo.status}
              </span>
              {canEdit && (
                <button
                  onClick={() => setIsEditingStatus(true)}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  <Edit className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Description</h2>
            <p className="text-gray-700 whitespace-pre-wrap">
              {todo.description || 'No description provided'}
            </p>
          </div>

          {/* Comments */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <MessageSquare className="w-5 h-5" />
                <span>Comments ({comments.length})</span>
              </h2>
            </div>

            <div className="space-y-4 mb-4">
              {comments.map((comment) => (
                <div key={comment.id || comment._id} className="border-l-4 border-blue-500 pl-4 py-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-gray-900">
                          {comment.user?.firstName} {comment.user?.lastName}
                        </span>
                        <span className="text-xs text-gray-500">
                          {moment(comment.createdAt).format('MMM D, YYYY [at] h:mm A')}
                        </span>
                      </div>
                      <p className="text-gray-700">{comment.comment}</p>
                    </div>
                    {(comment.userId === (user?.id || user?._id) || user?.role === 'Administrator') && (
                      <button
                        onClick={() => deleteComment(comment.id || comment._id)}
                        className="text-red-600 hover:text-red-700 ml-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={addComment}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Add Comment
              </button>
            </div>
          </div>

          {/* Attachments */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <Paperclip className="w-5 h-5" />
                <span>Attachments ({attachments.length})</span>
              </h2>
            </div>

            <div className="space-y-2 mb-4">
              {attachments.map((attachment) => (
                <div key={attachment.id || attachment._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3 flex-1">
                    <Paperclip className="w-5 h-5 text-gray-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {attachment.originalName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(attachment.fileSize / 1024).toFixed(2)} KB • 
                        Uploaded by {attachment.uploadedBy?.firstName} {attachment.uploadedBy?.lastName} • 
                        {moment(attachment.createdAt).format('MMM D, YYYY')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => downloadAttachment(attachment.id || attachment._id, attachment.originalName)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    {(attachment.uploadedBy?.id === (user?.id || user?._id) || user?.role === 'Administrator') && (
                      <button
                        onClick={() => deleteAttachment(attachment.id || attachment._id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {canEdit && (
              <div className="space-y-2">
                <input
                  id="fileInput"
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <input
                  id="fileDescription"
                  type="text"
                  placeholder="File description (optional)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <label
                  htmlFor="fileInput"
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  <span>{uploadingFile ? 'Uploading...' : 'Upload File'}</span>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Details</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Priority</label>
                <p className="mt-1 text-sm text-gray-900">{todo.priority}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Due Date</label>
                <p className="mt-1 text-sm text-gray-900">
                  {moment(todo.dueDate).format('MMM D, YYYY')}
                  {moment(todo.dueDate).isBefore(moment(), 'day') && todo.status !== 'Completed' && (
                    <span className="ml-2 text-red-600 font-medium">(Overdue)</span>
                  )}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Assigned To</label>
                <p className="mt-1 text-sm text-gray-900">
                  {todo.assignedTo?.firstName} {todo.assignedTo?.lastName}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Created By</label>
                <p className="mt-1 text-sm text-gray-900">
                  {todo.createdBy?.firstName} {todo.createdBy?.lastName}
                </p>
              </div>
              {todo.caseId && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Related Case</label>
                  <Link
                    to={`/cases/${todo.caseId.id || todo.caseId._id}`}
                    className="mt-1 text-sm text-blue-600 hover:text-blue-700 block"
                  >
                    {todo.caseId.caseNumber} - {todo.caseId.title}
                  </Link>
                </div>
              )}
              {todo.completedDate && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Completed Date</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {moment(todo.completedDate).format('MMM D, YYYY [at] h:mm A')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TodoDetail;


